import React from 'react';
import { connect } from 'react-redux';
import { IntlProvider } from 'react-intl';
import { get, find } from 'lodash';
import client from '../../lib/apolloClient';
import getMessages from '../../lib/i18n/getMessages';
import { CurrencyProvider } from '../../hooks/useCurrency';
import { GET_USER_PROFILE } from './SuccessionProfileDrawerQueries';
import SuccessionProfileDrawerNew from '../../components/Succession/ProfileDrawer/SuccessionProfileDrawerNew';
import { canViewProfile } from '../../lib/userUtils';
import { updateSelectedProfileDrawer, closeSuccessionDrawer, openSuccessionDrawer } from '../../actions/successionDrawerActionCreators';

// Initialize document.x if it doesn't exist
const initX = () => {
  if (!document.x) {
    document.x = {
      _a: null,
      listeners: [],
      registerListener: function(callback) {
        this.listeners.push(callback);
      }
    };
    
    Object.defineProperty(document.x, 'a', {
      get: function() {
        return this._a;
      },
      set: function(val) {
        this._a = val;
        this.listeners.forEach(callback => callback(val));
      }
    });
  }
};

class SuccessionDrawerWithListener extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      profileData: null,
      loading: false,
      error: null
    };
  }

  componentDidMount() {
    // Initialize document.x
    initX();
    console.log("SuccessionDrawerWithListener: document.x initialized", document.x);
    
    // Register listener for document.x.a changes
    document.x.registerListener((userName) => {
      console.log("SuccessionDrawerWithListener: document.x.a changed to", userName);
      if (userName) {
        console.log("SuccessionDrawerWithListener: opening drawer for", userName);
        this.props.openSuccessionDrawer(userName);
      }
    });
    
    this.fetchUserProfile();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.selectedUserName !== this.props.selectedUserName) {
      this.fetchUserProfile();
    }
  }

  fetchUserProfile = async () => {
    const { selectedUserName } = this.props;
    
    if (!selectedUserName) {
      this.setState({ profileData: null, loading: false, error: null });
      return;
    }
    
    this.setState({ loading: true });
    
    try {
      const result = await client.query({
        query: GET_USER_PROFILE,
        variables: { userName: selectedUserName },
        fetchPolicy: 'network-only'
      });
      
      // Log dealInterests to debug if available
      const dealInterests = get(result, 'data.getUser.inventory_user.deal_interests', []);
      if (dealInterests && dealInterests.length > 0) {
        console.log("SuccessionDrawerWithListener: User has deal interests:", dealInterests);
      }
      
      // Find active deal interest (if any)
      const activeDealInterest = this.findActiveDealInterest(dealInterests);
      
      this.setState({
        profileData: result.data,
        activeDealInterest,
        loading: false,
        error: null
      });
    } catch (error) {
      console.error('Error fetching user profile:', error);
      this.setState({
        profileData: null,
        activeDealInterest: null,
        loading: false,
        error
      });
    }
  };
  
  // Helper method to find an active deal interest
  findActiveDealInterest = (dealInterests) => {
    if (!dealInterests || !Array.isArray(dealInterests) || dealInterests.length === 0) return null;
    
    // First check if there's any active deal interest (not declined or archived)
    // Only users with active deal interests should display the Deal tab
    const activeDealInterest = dealInterests.find(interest => {
      if (!interest || typeof interest !== 'object') return false;
      
      const state = get(interest, 'workflow_state', '');
      const hasDeal = get(interest, 'deal') !== undefined && interest.deal !== null;
      
      return state !== 'declined' && state !== 'archived' && hasDeal;
    });
    
    return activeDealInterest || null;
  };

  render() {
    const { selectedUserName, updateSelectedProfileDrawer, closeSuccessionDrawer, activeDealInterest: propsActiveDealInterest } = this.props;
    const { profileData, activeDealInterest, loading, error } = this.state;

    // If no user is selected, don't render anything
    if (!selectedUserName) {
      return null;
    }

    // If loading with no data yet, don't render
    if (loading && !profileData) {
      return null;
    }

    // If there was an error, don't render
    if (error) {
      return null;
    }

    // If we have data, check if user is authorized
    const user = get(profileData, 'getUser');
    
    // If user doesn't exist, we should show a "user not found" message
    // Create a modified profileData object that won't crash the drawer
    let safeProfileData = profileData;
    if (!user && profileData) {
      safeProfileData = {
        ...profileData,
        getUser: {
          id: null,
          user_name: 'user-not-found',
          name: 'Profile Not Available',
          allowed_name: 'Profile Not Available',
          headline: 'This user does not have a complete profile in the system.',
          bio: 'This person has expressed interest in a partial sale but has not created a full FindBob profile yet.',
          notes: [],
          city: null,
          province: null,
          profile_picture: null
        }
      };
    }
    
    // Render the drawer if we have profile data
    if (!profileData) {
      return null;
    }

    // Prioritize activeDealInterest from props over the one found in profile data
    // This allows parent components (like BuyerRoom) to pass the buyer's deal interest
    const finalActiveDealInterest = propsActiveDealInterest || activeDealInterest;

    // Validate activeDealInterest before passing to component
    const validDealInterest = finalActiveDealInterest && 
      typeof finalActiveDealInterest === 'object' && 
      finalActiveDealInterest.deal && 
      typeof finalActiveDealInterest.deal === 'object' ? 
      finalActiveDealInterest : null;

    console.log('SuccessionDrawerWithListener: Deal interest priorities:', {
      propsActiveDealInterest: propsActiveDealInterest ? {
        id: propsActiveDealInterest.id,
        workflow_state: propsActiveDealInterest.workflow_state,
        inventory_user_id: propsActiveDealInterest.inventory_user?.user?.id
      } : null,
      stateActiveDealInterest: activeDealInterest ? {
        id: activeDealInterest.id,
        workflow_state: activeDealInterest.workflow_state,
        inventory_user_id: activeDealInterest.inventory_user?.user?.id
      } : null,
      finalActiveDealInterest: validDealInterest ? {
        id: validDealInterest.id,
        workflow_state: validDealInterest.workflow_state,
        inventory_user_id: validDealInterest.inventory_user?.user?.id
      } : null
    });

    // All conditions passed, render the drawer
    const locale = window.locale || 'en';
    return (
      <CurrencyProvider currency={window.currency || 'USD'}>
        <IntlProvider locale={locale} messages={getMessages(locale)}>
          <SuccessionProfileDrawerNew
            company={get(safeProfileData, 'company')}
            profile={{ data: safeProfileData, loading }}
            updateSelectedProfileDrawer={updateSelectedProfileDrawer}
            closeSuccessionDrawer={closeSuccessionDrawer}
            activeDealInterest={validDealInterest}
            currentUser={get(safeProfileData, 'getCurrentUser')}
            width="lg"
          />
        </IntlProvider>
      </CurrencyProvider>
    );
  }
}

const mapStateToProps = (state) => ({
  selectedUserName: get(state, 'successionDrawer.userName', null)
});

const mapDispatchToProps = {
  updateSelectedProfileDrawer,
  closeSuccessionDrawer,
  openSuccessionDrawer
};

export default connect(mapStateToProps, mapDispatchToProps)(SuccessionDrawerWithListener); 